import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskReveal } from "./Motion";
import { BRAND, MEDIA } from "../data/portfolio";

const ROOM_INITIAL = { y: "110%" };
const ROOM_ANIMATE = { y: "0%" };
const ROOM_TRANSITION = { duration: 0.9, delay: 0.65, ease: [0.22, 1, 0.36, 1] };

const scrollToId = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40 });
  else el.scrollIntoView({ behavior: "smooth" });
};

// A few words that sit inside the smoke ring on landing.
function HeroHeadline({ style }) {
  return (
    <motion.div
      style={style}
      className="absolute z-10 px-6 w-full flex flex-col items-center justify-center will-change-transform pointer-events-none"
    >
      <h1 className="text-center font-display font-extrabold text-white leading-[0.9] tracking-tighter text-4xl sm:text-5xl md:text-6xl max-w-[15ch]">
        <MaskReveal delay={0.35} lines={["Keynotes and"]} />
        <span className="block">
          <span className="reveal-line">
            <motion.span initial={ROOM_INITIAL} animate={ROOM_ANIMATE} transition={ROOM_TRANSITION} className="block">
              websites
            </motion.span>
          </span>
        </span>
        <motion.span
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="block font-serif-accent italic font-normal text-3xl sm:text-4xl md:text-5xl mt-4 text-white/90"
        >
          that make you look good.
        </motion.span>
      </h1>
    </motion.div>
  );
}

// Content that emerges on the other side of the portal: bio + calls to action.
function HeroEmerge({ style }) {
  return (
    <motion.div
      style={style}
      className="absolute z-20 px-6 w-full max-w-3xl mx-auto text-center flex flex-col items-center will-change-transform"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="h-px w-10 bg-white/40" />
        <span className="text-xs md:text-sm uppercase tracking-[0.3em] text-white/60">
          {BRAND.kicker} · {BRAND.role}
        </span>
        <span className="h-px w-10 bg-white/40" />
      </div>
      <p className="text-2xl md:text-3xl lg:text-4xl text-white/80 leading-snug font-display tracking-tight mb-6">
        Keynotes and websites that make you look as good as you are.
      </p>
      <p className="text-lg md:text-xl text-white/55 leading-relaxed max-w-2xl">
        Kaya Amah is a Florida-based creative strategist and designer of keynotes and websites for
        enterprise clients, including Oracle, Lenovo, Panasonic and Vital Farms, with 15+ years in
        creative direction and onsite support for last-minute edits.
      </p>
      <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
        <a
          href="#work"
          onClick={scrollToId("work")}
          data-testid="hero-see-work"
          className="lift inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 backdrop-blur-md px-7 py-3.5 text-sm text-white hover:bg-white hover:text-black transition-colors duration-300"
        >
          See the work
        </a>
        <a
          href="#contact"
          onClick={scrollToId("contact")}
          data-testid="hero-start-project"
          className="lift inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/15 backdrop-blur-md px-7 py-3.5 text-sm text-white/80 hover:text-white hover:border-white/40 transition-colors duration-300"
        >
          Start a project <ArrowUpRight size={16} />
        </a>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  // Portal fly-through: the ring + the few words zoom toward the viewer and pass;
  // on the other side the bio + CTAs emerge. Opacity/scale only, no blur.
  const ringScale = useTransform(scrollYProgress, [0, 1], [1, 4.8]);
  const ringOpacity = useTransform(scrollYProgress, [0, 0.55, 0.9], [0.95, 0.7, 0]);

  const headScale = useTransform(scrollYProgress, [0, 0.5], [1, 2.9]);
  const headOpacity = useTransform(scrollYProgress, [0, 0.32, 0.48], [1, 1, 0]);

  const emergeOpacity = useTransform(scrollYProgress, [0.5, 0.72], [0, 1]);
  const emergeScale = useTransform(scrollYProgress, [0.5, 0.92], [0.7, 1]);
  const emergeY = useTransform(scrollYProgress, [0.5, 0.92], ["9%", "0%"]);
  const emergePE = useTransform(scrollYProgress, (v) => (v > 0.55 ? "auto" : "none"));

  const hintOpacity = useTransform(scrollYProgress, [0, 0.12], [1, 0]);

  return (
    <section ref={ref} className="relative h-[220vh]" data-testid="hero">
      {/* Anchor lands the scroll where the bio paragraph is fully emerged */}
      <div id="about" aria-hidden className="absolute left-0 w-px h-px" style={{ top: "120vh" }} />
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden bg-[#050505]">
        {/* Smoke ring — the portal */}
        <motion.div style={{ scale: ringScale, opacity: ringOpacity }} className="absolute inset-0 z-0 will-change-transform">
          <img
            src={MEDIA.heroRing}
            alt="Cinematic ring of smoke on black, the hero visual for Kaya Amah's keynote and website design portfolio"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <HeroHeadline style={{ scale: headScale, opacity: headOpacity }} />
        <HeroEmerge style={{ opacity: emergeOpacity, scale: emergeScale, y: emergeY, pointerEvents: emergePE }} />

        <motion.div
          style={{ opacity: hintOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 text-white/40 text-xs uppercase tracking-widest"
        >
          <ArrowDown size={14} className="animate-bounce" /> Scroll through
        </motion.div>
      </div>
    </section>
  );
}
